/**
 * Runtime configuration, resolved once at module load.
 *
 * API base URL precedence:
 *   1. `?api=` query param (handy for testing against any backend)
 *   2. `VITE_API_URL` baked in at build time (per environment in CI)
 *   3. localhost fallback for `npm run dev`
 */
function resolveApiUrl(): string {
  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("api");
    if (override) return override.replace(/\/$/, "");
  }
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  return (fromEnv ?? "http://127.0.0.1:8010").replace(/\/$/, "");
}

export const API_URL = resolveApiUrl();

/** External link Yvett confirmed for "talk to someone instead". */
export const TALK_TO_SOMEONE_URL =
  "https://services.thejoyapp.com/en/llrrovingworkwellc/person_messages/new";

/**
 * Footer / legal links (WCAG 2.4.4 Link Purpose — no dead `#` anchors).
 * Canonical published URLs, confirmed 31 Jul 2026 by rendering both pages
 * (privacy policy last revised 4 Apr 2024; accessibility statement live —
 * DTAC D1.4.3). The earlier cerina.co/* pair was a best guess and 404s.
 *
 * Note: cerinahealth.com is a client-routed SPA, so these paths return a 404
 * *status* to a plain HTTP fetch while rendering correctly in a browser —
 * don't "fix" them on the strength of a curl.
 */
export const PRIVACY_POLICY_URL = "https://cerinahealth.com/privacy-policy/";
export const ACCESSIBILITY_STATEMENT_URL = "https://cerinahealth.com/accessibility/";

/**
 * Where a resident reports the page itself not working — a blank screen, a
 * button that does nothing, a question they cannot get past.
 *
 * A mailto rather than an in-page form (Naveen, 14 Sept): it ships today and
 * needs no backend. The trade-off is that the report arrives with no session
 * id, so the subject line carries a label the mailbox can filter on and the
 * resident is asked, in the body, to say what they were doing.
 *
 * This is NOT a clinical route. A resident in crisis has the Get Urgent Help
 * button, visible on every screen; the wording here stays deliberately about
 * the website so the two are never confused.
 */
export const TECH_SUPPORT_EMAIL = "wireworkwell@cerina.co";

/**
 * URL for a file in `public/`, resolved against the app's base path.
 *
 * Vite serves `public/` at the base, which is "/" in preview but "/llr/" in
 * production. A bare "logos/x.png" is *relative to the current URL*, so it
 * resolves differently on "/llr/" than on "/llr/gp/<token>" — and a leading
 * slash points outside the base entirely. Both failure modes are silent: the
 * SPA fallback answers with index.html and a 200, so the browser receives HTML
 * where it expected an image and simply renders nothing.
 */
export function asset(path: string): string {
  return `${import.meta.env.BASE_URL}${path.replace(/^\/+/, "")}`;
}
